import React, { useMemo, useState } from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useAxiosGet } from '../Hooks/HttpRequests';

export default function MatchupScores() {
  const [week, setWeek] = useState(1);

  // Sync default week to Sleeper NFL state
  const nflState = useAxiosGet('https://api.sleeper.app/v1/state/nfl');
  React.useEffect(() => {
    const w = nflState?.data?.week;
    if (w && Number(w) !== Number(week)) setWeek(Number(w));
  }, [nflState?.data?.week]);

  // Example league from prompt
  const leagueId = '1180948099673747456';
  const matchupUrl = `https://api.sleeper.app/v1/league/${leagueId}/matchups/${week}`;
  const rostersUrl = `https://api.sleeper.app/v1/league/${leagueId}/rosters`;
  const usersUrl = `https://api.sleeper.app/v1/league/${leagueId}/users`;

  const matchups = useAxiosGet(matchupUrl);
  const rosters = useAxiosGet(rostersUrl);
  const users = useAxiosGet(usersUrl);

  const nameByRosterId = useMemo(() => {
    const map = new Map();
    const rostersArr = Array.isArray(rosters.data) ? rosters.data : [];
    const usersArr = Array.isArray(users.data) ? users.data : [];
    const userById = new Map(usersArr.map((u) => [String(u.user_id), u]));
    rostersArr.forEach((r) => {
      const ownerId = r.owner_id != null ? String(r.owner_id) : null;
      const u = ownerId ? userById.get(ownerId) : null;
      const display = r?.metadata?.team_name || u?.metadata?.team_name || u?.display_name || `Roster ${r.roster_id}`;
      map.set(r.roster_id, display);
    });
    return map;
  }, [rosters.data, users.data]);

  const grouped = useMemo(() => {
    const rows = Array.isArray(matchups.data) ? matchups.data : [];
    const byMid = new Map();
    for (const m of rows) {
      const mid = m.matchup_id ?? `solo-${m.roster_id}`;
      if (!byMid.has(mid)) byMid.set(mid, []);
      byMid.get(mid).push(m);
    }
    const pairs = [];
    byMid.forEach((arr, key) => {
      const a = arr[0] || null;
      const b = arr[1] || null;
      pairs.push({ key, a, b });
    });
    pairs.sort((p1, p2) => {
      const t1 = (p1.a?.points || 0) + (p1.b?.points || 0);
      const t2 = (p2.a?.points || 0) + (p2.b?.points || 0);
      return t2 - t1;
    });
    return pairs;
  }, [matchups.data]);

  return (
    <div className="p-3">
      <div className="mb-3 flex items-center gap-3">
        <h2 className="text-xl font-semibold">Matchup Scores</h2>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="week-select-label">Week</InputLabel>
          <Select
            labelId="week-select-label"
            id="week-select"
            value={week}
            label="Week"
            onChange={(e) => setWeek(Number(e.target.value))}
          >
            {[...Array(18)].map((_, i) => (
              <MenuItem key={i + 1} value={i + 1}>
                {i + 1}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      {matchups.loading && <div className="p-2">Loading matchups…</div>}
      {matchups.error && <div className="bg-red-100 text-red-700 p-2 rounded">Failed to load matchup data.</div>}
      {!matchups.loading && !matchups.error && grouped.length === 0 && (
        <div className="p-2">No matchups found for week {week}.</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {grouped.map(({ key, a, b }) => {
          const nameA = a ? nameByRosterId.get(a.roster_id) || `Roster ${a.roster_id}` : 'TBD';
          const nameB = b ? nameByRosterId.get(b.roster_id) || `Roster ${b.roster_id}` : 'BYE';
          const ptsA = a?.points != null ? Number(a.points).toFixed(2) : '-';
          const ptsB = b?.points != null ? Number(b.points).toFixed(2) : '-';
          const winA = (a?.points || 0) > (b?.points || 0);
          const winB = (b?.points || 0) > (a?.points || 0);
          return (
            <div key={key} className="border rounded p-3 shadow-sm">
              {/* <div className="text-smmb-2">Matchup {String(a?.matchup_id ?? key)}</div> */}
              <div className="flex items-center justify-between mb-1">
                <div className={`font-medium ${winA ? 'text-green-700' : ''}`}>{nameA}</div>
                <div className={`tabular-nums ${winA ? 'font-bold' : ''}`}>{ptsA}</div>
              </div>
              <div className="flex items-center justify-between">
                <div className={`font-medium ${winB ? 'text-green-700' : ''}`}>{nameB}</div>
                <div className={`tabular-nums ${winB ? 'font-bold' : ''}`}>{ptsB}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
